import { useMemo, useState, useEffect } from 'react'
import { CNR } from 'constants/tokens'
import { ethers } from 'ethers'
import axios from 'axios'
import moment from 'moment'
import { useBlockNumber } from 'state/application/hooks'
import { BSC_API, BSC_API_KEY, RISE_ABI } from 'constants/config'
import { Burn, IBscEvent } from 'entities/Burn'
import { useContract } from './useContract'

function useBurnsQuery(querySize: number, maxResults: number): string {
  const [initialBlock, setInitialBlock] = useState(null)
  const currentBlock = useBlockNumber()
  const contract = useContract(CNR.address, RISE_ABI)

  useEffect(() => {
    if (!initialBlock) {
      setInitialBlock(currentBlock)
    }
  }, [initialBlock, currentBlock])

  return useMemo(() => {
    if (!querySize || !maxResults || !initialBlock || !contract) return undefined

    const filter = contract.filters.DoBalance()

    const toBlock = initialBlock
    const fromBlock = initialBlock - Math.floor((moment().unix() - moment().subtract({ days: 30 }).unix()) / 3)

    const queryString =
      `${BSC_API}/api` +
      '?module=logs' +
      '&action=getLogs' +
      `&address=${CNR.address}` +
      `&fromBlock=${fromBlock}` +
      `&toBlock=${toBlock}` +
      `&topic0=${filter.topics[0]}` +
      `&apikey=${BSC_API_KEY}`

    return queryString
  }, [querySize, maxResults, initialBlock, contract])
}

export default function useBurns(querySize: number, maxResults: number): null | (() => Promise<Burn[]>) {
  const query = useBurnsQuery(querySize, maxResults)
  return useMemo(() => {
    if (!query) {
      return undefined
    }

    return async function onLoad(): Promise<Burn[]> {
      const abiInterface = new ethers.utils.Interface(RISE_ABI)

      return axios
        .get(query)
        .then((response: any) => {
          const { data } = response
          const txns = data.result
            .map((event) => {
              const logDescription = abiInterface.parseLog({
                data: event.data,
                topics: event.topics,
              })
              return {
                ...event,
                ...logDescription,
              }
            })
            .map((txn: IBscEvent) => new Burn(txn))
            .sort((a: Burn, b: Burn) => b.block - a.block)

          return txns
        })
        .catch((error: any) => {
          throw new Error(`Get burns failed: ${error.message}`)
        })
    }
  }, [query])
}
